import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Trash2, Flag, ExternalLink, Loader2 } from 'lucide-react';

const STATUS_BADGE = {
  draft: { labelKey: 'services.statusDraft', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  active: { labelKey: 'services.statusActive', className: 'bg-green-50 text-green-700 border-green-200' },
  paused: { labelKey: 'services.statusPaused', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  archived: { labelKey: 'services.statusArchived', className: 'bg-red-50 text-red-700 border-red-200' },
  taken_down: { labelKey: 'services.statusTakenDown', className: 'bg-red-50 text-red-700 border-red-200' },
};

/**
 * Admin moderation queue for Pro service listings (W3, mirrors AdminBannersSection).
 * Lists GET /api/admin/pro-listings (reported-first, server-ordered) with an
 * open_report_count badge, owner/category, and a Takedown action that POSTs a reason
 * to /takedown. Re-fetches after action.
 */
const AdminServiceModerationSection = () => {
  const { t } = useTranslation();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [takedownTarget, setTakedownTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchListings = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) { setLoading(false); return; }
      const res = await api.get('/api/admin/pro-listings', {
        headers: { Authorization: `Bearer ${session.session.access_token}` },
      });
      setListings(res.data?.listings || []);
    } catch (error) {
      console.error('[AdminServiceModerationSection] Failed to fetch listings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, []);

  const openTakedown = (listing) => {
    setTakedownTarget(listing);
    setReason('');
  };

  const confirmTakedown = async () => {
    if (!takedownTarget || submitting) return;
    if (!reason.trim()) {
      toast.error(t('services.adminModReasonRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      await api.post(
        `/api/admin/pro-listings/${takedownTarget.id}/takedown`,
        { reason: reason.trim() },
        { headers: { Authorization: `Bearer ${session.session.access_token}` } }
      );
      toast.success(t('services.adminModTakedownSuccess'));
      setTakedownTarget(null);
      await fetchListings();
    } catch (error) {
      console.error('[AdminServiceModerationSection] Takedown failed:', error);
      toast.error(t('services.adminModActionError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card data-testid="table-pro-listings-moderation">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flag size={18} className="text-red-600" aria-hidden="true" />
            {t('services.adminModTitle')}{!loading ? ` (${listings.length})` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-8">{t('common.loading', 'Loading...')}</p>
          ) : listings.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">{t('services.adminModEmpty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <th className="py-3 px-4 text-left text-sm font-semibold">{t('services.adminModColTitle')}</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold">{t('services.adminModColOwner')}</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold">{t('services.adminModColStatus')}</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold">{t('services.adminModColReports')}</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold hidden md:table-cell">{t('services.adminModColCreated')}</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold">{t('services.adminModColActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {listings.map((l) => {
                    const badge = STATUS_BADGE[l.status] || STATUS_BADGE.draft;
                    const reported = (l.open_report_count || 0) > 0;
                    return (
                      <tr key={l.id} className={`hover:bg-muted/50 ${reported ? 'bg-red-50/60 dark:bg-red-950/20' : ''}`}>
                        <td className="py-2 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate max-w-[220px]" title={l.title}>{l.title}</span>
                            {l.slug && (
                              <a
                                href={`/services/listings/${l.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground hover:text-primary"
                                title={t('services.adminModViewListing')}
                              >
                                <ExternalLink size={13} aria-hidden="true" />
                              </a>
                            )}
                          </div>
                          {l.category_name && (
                            <span className="text-xs text-muted-foreground">{l.category_name}</span>
                          )}
                        </td>
                        <td className="py-2 px-4 text-sm text-muted-foreground truncate max-w-[160px]" title={l.owner_name || ''}>
                          {l.owner_name || '—'}
                        </td>
                        <td className="py-2 px-4">
                          <Badge variant="outline" className={badge.className}>{t(badge.labelKey)}</Badge>
                        </td>
                        <td className="py-2 px-4 text-sm">
                          {reported ? (
                            <Badge variant="destructive" className="text-xs flex items-center gap-1 w-fit" data-testid="listing-reported-badge">
                              <Flag size={10} aria-hidden="true" />
                              {l.open_report_count}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">0</span>
                          )}
                        </td>
                        <td className="py-2 px-4 text-xs text-muted-foreground hidden md:table-cell whitespace-nowrap">
                          {l.created_at ? new Date(l.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-2 px-4">
                          {l.status === 'taken_down' ? (
                            <span className="text-xs text-muted-foreground">{t('services.statusTakenDown')}</span>
                          ) : (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="text-xs whitespace-nowrap"
                              onClick={() => openTakedown(l)}
                              data-testid="listing-takedown-btn"
                            >
                              <Trash2 size={14} className="mr-1" aria-hidden="true" />
                              {t('services.adminModTakedown')}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Takedown confirm dialog — captures a reason. */}
      <Dialog open={!!takedownTarget} onOpenChange={(open) => { if (!open && !submitting) setTakedownTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 size={20} aria-hidden="true" />
              {t('services.adminModTakedownTitle')}
            </DialogTitle>
            <DialogDescription>{t('services.adminModTakedownBody')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-sm font-medium" htmlFor="listing-takedown-reason">
              {t('services.adminModReasonLabel')}
            </label>
            <Textarea
              id="listing-takedown-reason"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('services.adminModReasonPlaceholder')}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={submitting} onClick={() => setTakedownTarget(null)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={submitting}
              onClick={confirmTakedown}
              data-testid="confirm-listing-takedown-btn"
            >
              {submitting && <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />}
              {t('services.adminModTakedownAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminServiceModerationSection;
